<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useQuery } from '@tanstack/vue-query'
import { useI18n } from 'vue-i18n'
import { api } from '@/lib/api'
import { can, screenOn, session } from '@/lib/auth'
import Logo from '@/ui/Logo.vue'
import Icon from '@/ui/Icon.vue'
import Tooltip from '@/ui/Tooltip.vue'
import { matchNav, visibleNav, type MenuLayout, type NavGroup, type NavItem } from '../nav'
import HealthStatus from './HealthStatus.vue'

const p = defineProps<{ collapsed: boolean; mobileOpen: boolean }>()
const emit = defineEmits<{ toggleCollapsed: []; closeMobile: [viaKeyboard: boolean] }>()
const { t } = useI18n()
const route = useRoute()
const router = useRouter()
/* The role's own menu, as the company's admins arranged it (2026-10-22); the default one meanwhile or without. */
const roleMenu = useQuery({
  queryKey: ['auth', 'menu'],
  queryFn: () => api<{ layout: MenuLayout | null }>('/auth/menu'),
  staleTime: 5 * 60_000,
  retry: false,
})
const groups = computed(() => visibleNav(can, screenOn, { menu: true, layout: roleMenu.data.value?.layout ?? null }))
// Collapsing only applies on wide screens: the mobile drawer always shows labels.
const narrow = computed(() => p.collapsed && !p.mobileOpen)

// A section is active on its sub-pages too (/scripts/12), but /system has sub-screens of its own.
function active(i: NavItem): boolean {
  if (route.path === i.to) return true
  if (i.to === '/system') return false
  return route.path.startsWith(`${i.to}/`)
}

/*
 * Groups that fold (2026-10-22): the group of the screen open is unfolded, the others folded, until the
 * person opens or closes one; their choice is remembered on this browser.
 */
const FOLD_KEY = 'telsellhub.nav.groups'
function readFolds(): Record<string, boolean> {
  try { return JSON.parse(localStorage.getItem(FOLD_KEY) ?? '{}') as Record<string, boolean> } catch { return {} }
}
const folds = ref<Record<string, boolean>>(readFolds())
const holdsActive = (g: NavGroup) => g.items.some(active)
const isOpen = (g: NavGroup) => searching.value || (folds.value[g.key] ?? holdsActive(g))
function toggle(g: NavGroup) {
  folds.value = { ...folds.value, [g.key]: !isOpen(g) }
  try { localStorage.setItem(FOLD_KEY, JSON.stringify(folds.value)) } catch { /* kept for this visit */ }
}

/* « Aller à… »: the entries matching what is typed, every group unfolded; Enter opens the first. */
const query = ref('')
const searching = computed(() => query.value.trim() !== '')
const shown = computed(() => matchNav(groups.value, query.value, (i) => t(`nav.${i.key}.label`), (g) => t(`nav.groups.${g.key}`)))
function goFirst() {
  const first = shown.value[0]?.items[0]
  if (!first) return
  query.value = ''
  void router.push(first.to)
  emit('closeMobile', false)
}
function onSearchKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && searching.value) { e.stopPropagation(); query.value = '' }
}

/* The mobile drawer takes the focus when it opens, and gives it back on Escape. */
const nav = ref<HTMLElement | null>(null)
watch(() => p.mobileOpen, async (open) => {
  if (!open) return
  await nextTick()
  nav.value?.querySelector<HTMLElement>('a[href]')?.focus()
})
function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && p.mobileOpen) { e.stopPropagation(); emit('closeMobile', true) }
}
</script>

<template>
  <!-- Closed below lg, the drawer is off screen and out of the tab order (invisible). -->
  <nav id="shell-nav" ref="nav" :aria-label="t('shell.navLabel')" @click="emit('closeMobile', false)" @keydown="onKey"
       :class="['flex h-full flex-col border-e border-border bg-card',
                'max-lg:fixed max-lg:inset-y-0 max-lg:start-0 max-lg:z-drawer max-lg:w-nav max-lg:shadow-pop max-lg:transition-transform',
                mobileOpen ? '' : 'max-lg:invisible max-lg:-translate-x-full max-lg:rtl:translate-x-full']">
    <div class="flex h-topbar items-center gap-2.5 px-4">
      <Logo :url="session.org?.logoUrl" />
      <span v-if="!narrow" class="min-w-0 leading-tight">
        <strong class="block truncate text-[14px]">{{ session.org?.name || t('common.appName') }}</strong>
        <span class="block truncate text-[11.5px] text-muted-foreground">
          {{ session.org ? t('shell.brandSubOrg', { app: t('common.appName') }) : t('shell.brandSub') }}
        </span>
      </span>
    </div>

    <div class="flex-1 overflow-y-auto px-2 py-2">
      <label v-if="!narrow" class="mb-2 flex items-center gap-2 rounded-input border border-border bg-subtle px-2.5 focus-within:border-ring" @click.stop>
        <Icon name="search" :size="16" class="flex-none text-muted-foreground" />
        <input v-model="query" type="search" :placeholder="t('shell.navSearch')" :aria-label="t('shell.navSearchLabel')"
               class="h-10 min-w-0 flex-1 bg-transparent text-[14px] outline-none placeholder:text-muted-foreground"
               @keydown.enter.prevent="goFirst" @keydown="onSearchKey" />
      </label>
      <p v-if="!narrow && searching && !shown.length" class="px-2.5 py-2 text-[13px] text-muted-foreground">{{ t('shell.navNoMatch') }}</p>

      <!-- Folded: icons only, every screen, the groups set apart by a line. -->
      <template v-if="narrow">
        <div v-for="(g, n) in groups" :key="g.key" :class="['grid gap-0.5', n ? 'mt-2 border-t border-border pt-2' : '']">
          <Tooltip v-for="i in g.items" :key="i.key" :text="t(`nav.${i.key}.label`)" side="right">
            <RouterLink :to="i.to" :aria-current="active(i) ? 'page' : undefined" :aria-label="t(`nav.${i.key}.label`)"
                        :class="['flex h-10 items-center justify-center rounded-input transition-colors duration-fast', active(i) ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground']">
              <Icon :name="i.icon" :size="18" />
            </RouterLink>
          </Tooltip>
        </div>
      </template>

      <template v-else>
      <div v-for="g in shown" :key="g.key" class="mb-1.5">
        <button type="button" :aria-expanded="isOpen(g)" :disabled="searching"
                class="flex w-full items-center gap-2 rounded-input px-2.5 py-1.5 text-start text-[11px] font-semibold uppercase tracking-[.08em] text-muted-foreground transition-colors duration-fast enabled:hover:bg-muted enabled:hover:text-foreground"
                @click.stop="toggle(g)">
          <span class="min-w-0 flex-1 truncate">{{ t(`nav.groups.${g.key}`) }}</span>
          <span v-if="!isOpen(g)" class="text-[11px] font-medium normal-case tracking-normal">{{ g.items.length }}</span>
          <Icon v-if="!searching" name="chevron-down" :size="14" :class="['flex-none transition-transform duration-fast', isOpen(g) ? '' : '-rotate-90 rtl:rotate-90']" />
        </button>
        <div v-if="isOpen(g)" class="grid gap-0.5 pb-1">
          <RouterLink v-for="i in g.items" :key="i.key" :to="i.to" :aria-current="active(i) ? 'page' : undefined"
                      :class="['flex h-11 items-center gap-3 rounded-input px-2.5 text-[14px] font-medium transition-colors duration-fast',
                               active(i) ? 'bg-primary/10 text-primary' : 'text-foreground/80 hover:bg-muted hover:text-foreground']"
                      @click="query = ''">
            <Icon :name="i.icon" :size="18" />
            <span class="min-w-0 truncate">{{ t(`nav.${i.key}.label`) }}</span>
          </RouterLink>
        </div>
      </div>
      </template>
    </div>

    <div class="grid gap-1 border-t border-border p-2">
      <HealthStatus v-if="can('system.health.view')" :collapsed="narrow" />
      <button type="button" class="flex items-center gap-2 rounded-input px-2 py-1.5 text-[12px] text-muted-foreground hover:bg-muted max-lg:hidden"
              :aria-label="collapsed ? t('shell.expand') : t('shell.collapse')" @click.stop="emit('toggleCollapsed')">
        <Icon name="chevrons-left" :class="['transition-transform duration-slow ease-out', collapsed ? 'rotate-180' : '']" />
        <span v-if="!narrow">{{ t('shell.collapse') }}</span>
      </button>
    </div>
  </nav>
</template>
